/**
 * Ad Blocker Detection Hook
 * Detects if the user has an ad blocker that may interfere with error tracking
 */
import { useState, useEffect } from 'react';

interface AdBlockStatus {
  isBlocked: boolean;
  isChecking: boolean;
  dismissed: boolean;
}

const DISMISSED_KEY = 'adblock-warning-dismissed';
const DISMISSED_EXPIRY_HOURS = 24; // Don't show again for 24 hours after dismissal

export function useAdBlockDetection(): AdBlockStatus & { dismiss: () => void } {
  const [isBlocked, setIsBlocked] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if user previously dismissed the warning
    const dismissedData = localStorage.getItem(DISMISSED_KEY);
    if (dismissedData) {
      try {
        const { timestamp } = JSON.parse(dismissedData);
        const hoursSinceDismissal = (Date.now() - timestamp) / (1000 * 60 * 60);
        if (hoursSinceDismissal < DISMISSED_EXPIRY_HOURS) {
          setDismissed(true);
          setIsChecking(false);
          return;
        }
      } catch {
        localStorage.removeItem(DISMISSED_KEY);
      }
    }

    // Only check if Sentry DSN is configured
    const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
    if (!sentryDsn) {
      setIsChecking(false);
      return;
    }

    // Extract host from DSN
    let sentryHost: string;
    try {
      const dsnUrl = new URL(sentryDsn.replace(/^https?:\/\/[^@]+@/, 'https://'));
      sentryHost = dsnUrl.origin;
    } catch {
      setIsChecking(false);
      return;
    }

    // Test if we can reach Sentry
    const testAdBlocker = async () => {
      try {
        // Try to fetch a tiny request to Sentry
        // We use a HEAD request to minimize data transfer
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);

        await fetch(`${sentryHost}/api/0/`, {
          method: 'HEAD',
          mode: 'no-cors', // Allows the request to go through even without CORS
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // If we get here without error, ad blocker likely isn't blocking
        // Note: no-cors requests don't give us response info, but if blocked,
        // they throw an error
        setIsBlocked(false);
      } catch (error) {
        // Request was blocked or failed
        if (error instanceof Error) {
          // Check if it's a network error (likely ad blocker)
          if (error.name === 'TypeError' || error.message.includes('Failed to fetch')) {
            setIsBlocked(true);
          }
        }
      } finally {
        setIsChecking(false);
      }
    };

    // Small delay to not impact initial page load
    const timer = setTimeout(testAdBlocker, 2000);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify({ timestamp: Date.now() }));
  };

  return { isBlocked, isChecking, dismissed, dismiss };
}
